<template>
  <article
    class="company-page"
    :class="{ 'company-page--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="company-title"
  >
    <div class="company-layout">
      <aside class="company-index">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>On this page</summary>
          <nav aria-label="Company sections">
            <a href="#company-title">Digital Fusion Inc.</a>
            <a href="#digital-storytelling">Digital storytelling</a>
            <a href="#self-biography">And now, Self Biography</a>
          </nav>
        </details>
      </aside>
      <div class="company-body">
        <header>
          <p class="company-eyebrow">
            The company behind Self Biography
          </p>
          <h1 id="company-title">
            Digital Fusion Inc.
          </h1>
          <p class="company-lead">
            More than forty years of ideas, innovation and digital storytelling.
          </p>
          <p><strong>Digital Fusion Inc. (DFI)</strong> was founded by <strong>Pepe Moreno</strong> more than forty years ago, at the beginning of the personal-computer revolution and long before today's digital world became part of everyday life.</p>
          <p>From the beginning, Digital Fusion has been an <strong>ideas company</strong>—exploring how technology can create new forms of entertainment, communication, storytelling and human experience.</p>
          <p>Over the decades, DFI's work has crossed traditional and digital art, comic books and graphic storytelling, computer entertainment, video games, interactive media, publishing, applications, and emerging technologies.</p>
          <p>The underlying idea has remained remarkably consistent:</p>
          <p class="company-statement">
            <strong>Technology changes. Ideas move everything forward.</strong>
          </p>
        </header>
        <section
          class="company-section"
          aria-labelledby="digital-storytelling"
        >
          <h2 id="digital-storytelling">
            From digital storytelling to interactive worlds
          </h2>
          <p>Digital Fusion has spent its history experimenting with the space where <strong>art, storytelling and technology meet</strong>.</p>
          <p>That philosophy has taken the company through computer-generated comics and digital storytelling, video games and interactive entertainment, and increasingly sophisticated ways for people to participate in stories rather than simply watch or read them.</p>
          <p>Today, artificial intelligence opens another extraordinary chapter in that evolution.</p>
        </section>
        <section
          class="company-section"
          aria-labelledby="self-biography"
        >
          <h2 id="self-biography">
            And now, Self Biography
          </h2>
          <p>Self Biography grew naturally from that history.</p>
          <p>Instead of inventing another fictional world for people to enter, we began asking a different question:</p>
          <p class="company-statement">
            <strong>What if the most interesting world we could explore was our own?</strong>
          </p>
          <p>Our memories. Our photographs. Our families. Our travels. Our accomplishments. Our mistakes. Our work. Our wisdom. The people we have known and the experiences that made us who we are.</p>
          <p>Self Biography brings together decades of experience in <strong>visual storytelling, interactive entertainment, software, digital media and emerging technology</strong> and applies them to something much more personal:</p>
          <p class="company-emphasis">
            <strong>The story of a human life.</strong>
          </p>
        </section>
        <footer class="company-closing">
          <p>Self Biography is developed and operated by <strong>Digital Fusion Inc.</strong></p>
          <p class="company-closing-message">
            <strong>The technology is new.<br />The pursuit of new ideas isn't.</strong>
          </p>
          <div class="company-links">
            <a
              href="https://www.digitalfusiongames.com/"
              target="_blank"
              rel="noopener noreferrer"
            >Visit Digital Fusion Inc. <span class="company-link-note">(opens in a new tab)</span> ↗</a>
            <router-link to="/pages/self-biography">
              About Self Biography →
            </router-link>
          </div>
        </footer>
      </div>
    </div>
  </article>
</template>
<script>
export default {
  name: 'SelfBiographyCompany',
  data: () => ({ previousTitle: '' }),
  mounted() {
    this.previousTitle = document.title
    document.title = 'Digital Fusion Inc. | Self Biography'
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const target = ['company-title', 'digital-storytelling', 'self-biography'].includes(id) && document.getElementById(id)
      if (target) target.scrollIntoView()
      else window.scrollTo(0, 0)
    })
  },
  beforeDestroy() { document.title = this.previousTitle },
}
</script>
<style lang="scss" scoped>
.company-page {
  --company-text: #5e5669; --company-heading: #393345; --company-accent: #713ac7; --company-surface: #fff; --company-tint: #f3eefb; --company-divider: rgba(94,86,105,.16);
  max-width: 1160px; margin: 24px auto 48px; color: var(--company-text); font-size: 16px; line-height: 1.8; text-align: left;
  &--dark { --company-text: #d0cadf; --company-heading: #eee9fa; --company-accent: #c6a5ff; --company-surface: #312d4b; --company-tint: #39304f; --company-divider: rgba(231,227,252,.16); }
  h1,h2 { color: var(--company-heading); font-weight: 600; line-height: 1.3; text-align: left; scroll-margin-top: 88px; }
  h1 { font-size: clamp(2rem,3.7vw,3rem); letter-spacing: -.035em; margin-bottom: 18px; }
  h2 { font-size: clamp(1.4rem,2.3vw,1.8rem); letter-spacing: -.02em; margin-bottom: 20px; }
  p { margin-bottom: 18px; }
  strong { font-weight: 600; }
  a { color: var(--company-accent); text-underline-offset: 4px; }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--company-accent); outline-offset: 4px; }
  .company-eyebrow { color: var(--company-accent); font-size: .75rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 14px; }
}
.company-layout { display: grid; grid-template-columns: 200px minmax(0,1fr); gap: 32px; align-items: start; }
.company-index { position: sticky; top: 88px; }
.company-index summary { font-weight: 600; cursor: pointer; min-height: 44px; }
.company-index nav { display: flex; flex-direction: column; border-left: 1px solid var(--company-divider); padding-left: 16px; }
.company-index a { display: flex; align-items: center; min-height: 44px; padding: 6px 0; font-size: .875rem; line-height: 1.5; text-decoration: none; }
.company-index a:hover { text-decoration: underline; }
.company-body { min-width: 0; background: var(--company-surface); border: 1px solid var(--company-divider); border-radius: 8px; padding: 44px; }
.company-lead { font-size: 1.2rem; line-height: 1.6; color: var(--company-heading); margin-bottom: 28px !important; }
.company-statement { font-size: clamp(1.15rem,1.7vw,1.4rem); line-height: 1.6; color: var(--company-accent); background: var(--company-tint); padding: 22px 24px; border-radius: 6px; margin: 24px 0; }
.company-section { margin-top: 36px; padding-top: 36px; border-top: 1px solid var(--company-divider); }
.company-section > :last-child { margin-bottom: 0; }
.company-emphasis { font-size: 1.3rem; color: var(--company-accent); }
.company-closing { margin-top: 36px; padding-top: 28px; border-top: 1px solid var(--company-divider); }
.company-closing-message { color: var(--company-heading); font-size: 1.15rem; line-height: 1.6; }
.company-links { display: flex; flex-wrap: wrap; gap: 12px 24px; margin-top: 24px; }
.company-links a { padding: 6px 0; min-height: 44px; font-size: .875rem; }
.company-link-note { font-size: .75rem; }
@media(max-width:959px) {
  .company-layout { grid-template-columns: minmax(0,1fr); gap: 20px; }
  .company-index { position: static; }
  .company-index summary { padding: 8px 0; }
  .company-body { padding: 28px; }
}
@media(max-width:599px) {
  .company-page { margin: 0 auto 32px; }
  .company-body { padding: 24px 0 0; border: 0; border-top: 1px solid var(--company-divider); border-radius: 0; background: transparent; }
  .company-section { margin-top: 28px; padding-top: 28px; }
  .company-statement { padding: 20px; }
  .company-links { flex-direction: column; gap: 8px; }
}
</style>
